import { useGet } from './useFetch'
import { useAuth } from './useAuth'
import { ApiResponse } from '@magnetar/admin-api-interfaces'

export type AppointmentMode = 'DEFAULT' | 'SHARED_ROOMS'

export interface AppointmentSettingMetadata {
  sequential_type?: 'REGULAR' | 'ODD_EVEN'
  start_number?: number
  increment_by?: number
  online_even?: boolean
  walk_in_odd?: boolean
  online_number_type?: 'EVEN' | 'ODD'
  time_slot_type?: 'FREE' | 'FIXED'
  slot_duration_minutes?: number
  buffer_minutes?: number
  allow_overlap?: boolean
}

export interface AppointmentSetting {
  appointment_setting_id: number
  clinic_id: number
  appointment_mode: AppointmentMode
  doctor_id?: number
  appointment_type: 'SEQUENTIAL' | 'TIME_BASED'
  metadata: AppointmentSettingMetadata
  day_start_time?: string
  day_end_time?: string
  max_appointments?: number
  is_active: boolean
  created_at: string
  updated_at: string
  examination_rooms?: Array<{
    examination_room_id: number
    name: string
  }>
  doctor?: {
    doctor_id: number
    name: string
    specialty: string[]
  }
}

interface UseAppointmentSettingParams {
  examinationRoomId?: number
}

export function useAppointmentSetting(params: UseAppointmentSettingParams) {
  const { token } = useAuth()

  // 使用明確的路由路徑
  const url =
    token && params.examinationRoomId
      ? `/api/examination-room/${params.examinationRoomId}/appointment-setting`
      : null

  const { data, error, mutate } = useGet<ApiResponse<AppointmentSetting>>(
    url,
    {}, // 不需要查詢參數，因為診間 ID 已經在路徑中
    token ?? '',
  )

  return {
    // 將單個對象轉換為陣列，以保持前端使用一致性
    appointmentSettings: data?.data ? [data.data] : [],
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

// 根據診間 ID 獲取預約掛號設定
export function useExaminationRoomAppointmentSetting(examinationRoomId?: number) {
  return useAppointmentSetting({ examinationRoomId })
}

// 取得診所下所有 active 預約掛號設定（共用模式查詢用）
export function useClinicAppointmentSettings(clinicId?: number) {
  const { token } = useAuth()
  const url = token && clinicId ? `/api/appointment-setting/by-clinic/${clinicId}` : null
  const { data, error, mutate } = useGet<ApiResponse<AppointmentSetting[]>>(
    url,
    {},
    token ?? '',
  )
  return {
    settings: data?.data ?? [],
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}
